import * as Yup from "yup";
import moment from "moment";

/**
 * RoadZen Users page — the words and checks, kept here so they are tested on their own (roadzenUsersUtils.test.js).
 * Only ONE RoadZen user is active at a time (owner, 2026-11-03): the confirm box says who is switched off.
 */

export const MOBILE_RE = /^[6-9]\d{9}$/;

/** The Create User form's checks (the server checks the same again). */
export const createUserSchema = Yup.object().shape({
  name: Yup.string().trim().required("Enter the name").max(100, "The name can be 100 letters at most"),
  mobile: Yup.string()
    .trim()
    .required("Enter the mobile number")
    .matches(MOBILE_RE, "Enter a 10 digit mobile number starting with 6, 7, 8 or 9"),
  email: Yup.string().trim().email("Enter a valid email or leave it empty").max(100, "The email can be 100 letters at most"),
  address: Yup.string().trim().max(200, "The address can be 200 letters at most"),
  city: Yup.string().trim().max(60, "The city can be 60 letters at most"),
  state: Yup.string().trim().max(60, "The state can be 60 letters at most"),
});

export const EMPTY_USER = { name: "", mobile: "", email: "", address: "", city: "", state: "" };

/** Only what was typed is sent. */
export const userPayload = (values) => {
  const out = {};
  for (const key of Object.keys(EMPTY_USER)) {
    const value = String(values[key] == null ? "" : values[key]).trim();
    if (value) out[key] = value;
  }
  return out;
};

const names = (users) => users.map((u) => u.name).join(", ");

/**
 * The confirm box for Activate / Deactivate (UiComponents/ConfirmBox: `content` is the question, `subContent` the line
 * under it, `title` + `confirmButton` only pick the colour): who is switched on and who is switched off.
 */
export const confirmFor = (row, users, active) => {
  const othersActive = (users || []).filter((u) => u.isActive && u.id !== row.id);
  if (active) {
    return {
      // "Active" (not "Activate") is the word the box turns green for, like the User page's Active box
      title: "Active",
      content: `Activate ${row.name}?`,
      subContent: othersActive.length
        ? `${names(othersActive)} will be switched off first. Only one RoadZen user can be active.`
        : `${row.name} will be the active RoadZen user.`,
      confirmButton: "Activate",
    };
  }
  return {
    title: "Deactivate",
    content: `Deactivate ${row.name}?`,
    subContent: othersActive.length ? `${names(othersActive)} stays active.` : "No RoadZen user will be active until you activate one.",
    confirmButton: "Deactivate",
  };
};

/** The line above the table when RoadZen does not show exactly one active user (null when it does). */
export const activeWarning = (users) => {
  if (!users) return null;
  const active = users.filter((u) => u.isActive);
  if (active.length === 1) return null;
  if (!active.length) return users.length ? "No RoadZen user is active. Activate one to use RoadZen." : null;
  return `${active.length} RoadZen users are active (${names(active)}). Activate the one to keep and the others are switched off.`;
};

export const walletText = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? "—"
    : `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const dateText = (value) => (value && moment(value).isValid() ? moment(value).format("DD-MM-YYYY HH:mm") : "—");

/** One history row in plain words: a short title, its colour, and the result. */
const ACTION_TITLE = { CREATE_USER: "Created", ACTIVATE_USER: "Activated", DEACTIVATE_USER: "Deactivated" };
const ACTION_TRIED = { CREATE_USER: "Create", ACTIVATE_USER: "Activate", DEACTIVATE_USER: "Deactivate" };
export const RESULT_LABEL = {
  DONE: "Done",
  PARTLY_DONE: "Partly done",
  REFUSED: "Not done",
  UNKNOWN: "Not known",
  IN_PROGRESS: "Running",
};
export const RESULT_TONE = { DONE: "green", PARTLY_DONE: "amber", REFUSED: "red", UNKNOWN: "amber", IN_PROGRESS: "navy" };

const money = (paise) =>
  typeof paise === "number" ? ` ₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";

export const historyTitle = (row = {}) => {
  const who = row.userName || "a user";
  // Wallet page: money for the active user
  if (row.action === "TEST_CREDIT") return `Test credit${money(row.amountPaise)} to ${who}`;
  if (row.action === "TOPUP_LINK") return `Payment link${money(row.amountPaise)} for ${who}`;
  if (row.result === "DONE" || row.result === "PARTLY_DONE") return `${ACTION_TITLE[row.action] || "Changed"} ${who}`;
  return `${ACTION_TRIED[row.action] || "Change"} ${who}`;
};
